import { SiteUrls } from '@tableau/shared-urls';
import { SagaIterator } from 'redux-saga';
import {
  all,
  call,
  fork,
  put,
  select,
  takeEvery
  } from 'redux-saga/effects';

import { WindowLocationService } from '~src/commonInfra/services/WindowLocationService';

import { requestFailed } from '../../app/actions/RequestFailedAction';
import { deleteContentAction } from '../../app/actions/spaces/DeleteContentAction';
import { requestSpaceContents } from '../../app/actions/spaces/RequestSpaceContentsActions';
import { ResumeMetricErrorDialogProps, resumeMetricErrorModalOptions } from '../../app/content/ResumeMetricErrorDialog';
import { siteUrlsSelector } from '../../app/selectors/SiteUrlsSelectors';
import { UrlParamKeys } from '../../app/UrlParamKeys';
import { Messages } from '../../codegen/Localize';
import { ModalInstance, ReactModalService } from '../../commonInfra/dialogs/ReactModalService';
import { DeleteContentsAction } from '../../content/services/DeleteContentsAction';
import WebHttpClient from '../../httpClient/WebHttpClient';
import { getSessionManagementServiceInstance } from '../../sessionManagement/SessionManagementService';
import { IContentInfo } from '../../ts/models/IContentInfo';
import { SetShowMetricsBannerRequest } from '../../ts/ServerApi/requests/SetShowMetricsBannerRequest';
import {
  HideMetricsBanner,
  HideMetricsBannerAction,
  ResumeMetric,
  resumeMetric,
  ResumeMetricAction as ResumeMetricReduxAction,
  ResumeMetricFromExplorer,
  ResumeMetricFromExplorerAction,
} from '../actions/UpdateMetricsActions';
import { IMetricInfo } from '../models/IMetricInfo';
import { Metrics } from '../resources/Metrics';
import { ResumeMetricAction } from '../services/ResumeMetricAction';
import { metricsDataSaga } from './MetricsDataSaga';

export function* handleResumeMetric(action: ResumeMetricReduxAction) {
  try {
    yield call(resumeSuspendedMetric, action.metric, action.isOwner);
    yield call(refreshAfterMetricsActionSucceeded);
  } catch(e) {
    yield call(deleteSuspendedMetric, action.metric, e.message);
  }
}

export function* handleResumeMetricFromExplorer(action: ResumeMetricFromExplorerAction): SagaIterator {
  try {
    const [ metricInfo ] = (yield all([
      call(getMetricInfo, action.metricId),
      call(navigateToMetricDetailPage, action.metricId)
    ])) as any; // TFS 1116084 - need to upgrade redux-saga in order to remove 'as any'
    yield put(resumeMetric(metricInfo, action.isOwner));
  } catch (e) {
    yield put(requestFailed(e, Messages.unknownFailure()));
  }
}

export function* handleHideMetricsBanner(action: HideMetricsBannerAction) {
  try {
    yield call(dismissMetricsBanner);
    yield call(updateSessionInfo);
  } catch (e) {
    yield put(requestFailed(e, Messages.unknownFailure()));
  }
}

export function resumeSuspendedMetric(metric: IMetricInfo, isOwner: boolean) {
  return ResumeMetricAction.resumeMetric(metric, isOwner);
}

export function* refreshAfterMetricsActionSucceeded() {
  yield put(requestSpaceContents());
}

export function* deleteSuspendedMetric(metric: IMetricInfo, errorMessage: string) {
  try {
    const shouldDelete: boolean = yield call(openMetricResumeErrorDialog, errorMessage);
    if (!shouldDelete) {
      return;
    }
    yield put(deleteContentAction(metric as IContentInfo));
  } catch(e) {
    yield put(requestFailed(e, Messages.deleteFailed()));
  }
}

export function confirmDeleteMetric(metric: IMetricInfo) {
  return DeleteContentsAction.confirmDeleteContents([metric as IContentInfo]);
}

export function openMetricResumeErrorDialog(errorMessage: string): Promise<boolean> {
  let modalInstance: ModalInstance<boolean>;

  const props: ResumeMetricErrorDialogProps = {
    submit: () => modalInstance && modalInstance.close(true),
    cancel: () => modalInstance && modalInstance.close(false),
    errorMessage: errorMessage
  };

  modalInstance = ReactModalService.open(resumeMetricErrorModalOptions(props));
  return modalInstance.result;
}

export function* navigateToMetricDetailPage(metricId: string) {
  const urls: SiteUrls = yield select(siteUrlsSelector);
  const metricPath = urls.metricUrl(metricId);
  WindowLocationService.navigateTo(metricPath);
  WindowLocationService.updateHashQueryParameterWithReplace(UrlParamKeys.SuppressResumeMetricBanner, 't');
}

export async function getMetricInfo(metricId: string): Promise<IMetricInfo> {
  return await Metrics.getById(metricId);
}

export function dismissMetricsBanner() {
  return WebHttpClient.sendRequest(new SetShowMetricsBannerRequest({ showMetricsBanner: false }));
}

export function updateSessionInfo() {
  const sessionManagementService = getSessionManagementServiceInstance();
  return sessionManagementService.refreshSessionInfo();
}

export function* rootMetricSpaceSagas(): SagaIterator {
  yield takeEvery(ResumeMetric, handleResumeMetric);
  yield takeEvery(ResumeMetricFromExplorer, handleResumeMetricFromExplorer);
  yield takeEvery(HideMetricsBanner, handleHideMetricsBanner);
  yield fork(metricsDataSaga);
}
